import { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Paper,
  Typography,
  Stack,
  IconButton,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useDropzone } from 'react-dropzone';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
} from 'material-react-table';

import { formatDateWithTime, useIsMediaQuery } from '@/shared';
import { useFetchDeleteDocumentacion, useFetchPostDocumentacionArchivos } from '@/shared/actions/administracion/documentacion.actions';
import { Documentacion, DocumentacionMultipleResponse } from '@/shared/interfaces/erp/documentos/Documentacion';
import { Contrato } from '@/shared/interfaces';
import { useUiConfirmModalStore } from '@/store/ui';
import { CloudDownload } from '@mui/icons-material';
import { useDocumentacionContratoStore } from '../../hooks/documentacion-contrato.store';


type TabSubirDocumentacionInstallProps = {
  contrato: Contrato
};

export const TabSubirDocumentacionInstall =
  ({
    contrato
  }: TabSubirDocumentacionInstallProps) => {
    //const [documentos, setDocumentos] =
    //  useState<Documentacion[]>(contrato.documentacion_data || []);

    const documentos = useDocumentacionContratoStore((s) => s.documentos);
    const setDocumentos = useDocumentacionContratoStore((s) => s.setDocumentos);
    const removeDocumento = useDocumentacionContratoStore((s) => s.removeDocumento);

    const [archivosPendientes, setArchivosPendientes] = useState<File[]>([]);
    const isMobile = useIsMediaQuery('sm');

    const { mutate: subirDocumentos, isPending: isSubiendo } =
      useFetchPostDocumentacionArchivos();

    const { mutate: eliminarDocumentacion } = useFetchDeleteDocumentacion();

    const setConfirmDialog = useUiConfirmModalStore((s) => s.setConfirmDialog);
    const setConfirmDialogIsOpen = useUiConfirmModalStore(
      (s) => s.setConfirmDialogIsOpen
    );

    const formatearTamanio = (bytes: number): string => {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
    };

    function kbToMb(kb: number): number {
      return Number((kb / 1024).toFixed(2));
    }

    const onDrop = useCallback((acceptedFiles: File[]) => {
      setArchivosPendientes((prev) => [...prev, ...acceptedFiles]);
    }, []);

    const { getRootProps, getInputProps, isDragActive, fileRejections } =
      useDropzone({
        onDrop,
        accept: { 'application/pdf': ['.pdf'] },
        multiple: true,
      });

    const handleSubirDocumentos = () => {
      if (archivosPendientes.length === 0 || isSubiendo) return;

      subirDocumentos(
        {
          files: archivosPendientes,
          modelo: 'CONTRATO',
          referencia_modelo: contrato.codigo,
        },
        {
          onSuccess: (data: DocumentacionMultipleResponse) => {
            const documentosResponse = data.data.creados;
            const newDocumentos = [...documentos, ...documentosResponse];
            setDocumentos(newDocumentos);
            setArchivosPendientes([]);
          },
        }
      );
    };

    const handleLimpiarPendientes = () => {
      setArchivosPendientes([]);
    };

    const handleRemoverPendiente = (index: number) => {
      setArchivosPendientes((prev) => prev.filter((_, i) => i !== index));
    };

    const handleEliminar = (id: string) => {
      setConfirmDialog({
        isOpen: true,
        title: 'Eliminar documento',
        subtitle: '¿Está seguro de que desea eliminar este documento? Esta acción no se puede deshacer.',
        onConfirm: () => {
          eliminarDocumentacion(id, {
            onSuccess: () => {
              removeDocumento(id);
            },
          });
          setConfirmDialogIsOpen(false);
        },
        onClose: () => setConfirmDialogIsOpen(false),
        cancelTextBtn: 'Cancelar',
        confirmTextBtn: 'Eliminar',
      });
    };

    const handleVer = (url: string) => {
      window.open(url, '_blank', 'noopener,noreferrer');
    };

    const handleDescargar = async (url: string, nombre: string) => {
      try {
        const response = await fetch(url);
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = nombre;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      } catch (error) {
        console.error('Error al descargar el documento:', error);
      }
    };

    const columns = useMemo<MRT_ColumnDef<Documentacion>[]>(
      () => [
        {
          accessorKey: 'nombre',
          header: 'Nombre',
        },
        {
          accessorKey: 'tamanio',
          header: 'Tamaño',
          Cell: ({ row }) => `${kbToMb(Number(row.original.size))} MB`
        },
        {
          accessorKey: 'fecha_creacion',
          header: 'Fecha Creación',
          Cell: ({ row }) => formatDateWithTime(row.original.fecha_creacion)
        },
        {
          accessorKey: 'usuario',
          header: 'Usuario',
        },
        {
          id: 'acciones',
          header: 'Acciones',
          Cell: ({ row }) => (
            <Stack direction="row" spacing={1}>
              <IconButton
                color="primary"
                onClick={() => handleVer(row.original.url!)}
                title="Visualizar"
                size={isMobile ? 'small' : 'medium'}
              >
                <VisibilityIcon fontSize={isMobile ? 'small' : 'medium'} />
              </IconButton>
              <IconButton
                color="primary"
                onClick={() => handleDescargar(row.original.url!, row.original.nombre)}
                title="Descargar"
                size={isMobile ? 'small' : 'medium'}
              >
                <CloudDownload fontSize={isMobile ? 'small' : 'medium'} />
              </IconButton>
              <IconButton
                color="error"
                onClick={() => handleEliminar(row.original.id_documentacion)}
                title="Eliminar"
                size={isMobile ? 'small' : 'medium'}
              >
                <DeleteIcon fontSize={isMobile ? 'small' : 'medium'} />
              </IconButton>
            </Stack>
          ),
        },
      ],
      [isMobile]
    );

    const table = useMaterialReactTable({
      columns,
      data: documentos,
      enableColumnActions: false,
      enableColumnFilters: false,
      enablePagination: false,
      enableSorting: false,
      enableBottomToolbar: false,
      enableTopToolbar: false,
      enableRowNumbers: true,
      muiTableContainerProps: {
        sx: {
          maxHeight: { xs: '300px', md: '400px' },
          overflowX: 'auto',
        },
      },
      state: {
        columnVisibility: isMobile
          ? { id: false, fecha: false, usuario: false }
          : {},
      },
    });

    return (
      <Box sx={{ width: '100%', p: { xs: 1, sm: 1.5, md: 2 } }}>
        <Paper
          {...getRootProps()}
          variant="outlined"
          sx={{
            width: '100%',
            p: { xs: 2, sm: 3, md: 4 },
            mb: { xs: 2, md: 3 },
            border: '2px dashed',
            borderColor: isDragActive ? 'primary.main' : 'grey.400',
            backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
            cursor: 'pointer',
            textAlign: 'center',
            transition: 'all 0.2s ease-in-out',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: { xs: 120, sm: 160 },
          }}
        >
          <input {...getInputProps()} />
          <CloudUploadIcon
            sx={{
              fontSize: { xs: 36, md: 48 },
              color: 'primary.main',
              mb: 1,
            }}
          />
          <Typography
            variant="h6"
            color="textPrimary"
            sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}
          >
            {isDragActive
              ? 'Suelta los archivos aquí...'
              : 'Arrastra y suelta archivos PDF aquí, o haz clic para seleccionar'}
          </Typography>
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{ mt: 1, fontSize: { xs: '0.75rem', md: '0.875rem' } }}
          >
            Solo se permiten archivos PDF
          </Typography>
        </Paper>

        {fileRejections.length > 0 && (
          <Typography color="error" sx={{ mb: 2 }}>
            Algunos archivos no son PDF y fueron rechazados.
          </Typography>
        )}

        {archivosPendientes.length > 0 && (
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Archivos listos para subir
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              useFlexGap
              sx={{ mb: 2 }}
            >
              {archivosPendientes.map((file, index) => (
                <Chip
                  key={`${file.name}-${index}`}
                  label={`${file.name} (${formatearTamanio(file.size)})`}
                  size="small"
                  onDelete={() => handleRemoverPendiente(index)}
                  deleteIcon={<CloseIcon />}
                />
              ))}
            </Stack>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
            >
              <Button
                variant="contained"
                color="primary"
                startIcon={<UploadFileIcon />}
                onClick={handleSubirDocumentos}
                disabled={isSubiendo}
                fullWidth={isMobile}
              >
                {isSubiendo ? 'Subiendo...' : 'Subir documentos'}
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                onClick={handleLimpiarPendientes}
                disabled={isSubiendo}
                fullWidth={isMobile}
              >
                Limpiar
              </Button>
            </Stack>
          </Paper>
        )}

        <MaterialReactTable table={table} />
      </Box>
    );
  };
